import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button, LoadingState } from '../components/ui';
import { CATEGORIES } from '../constants/categories';
import { getNewSeries, getTrendingSeries, getSeriesCoverUrls, getUserSeries } from '../services/seriesService';
import SeriesCard from '../components/series/SeriesCard';
import { CreateEpisodeModal, CreateSeriesModal } from '../components/series';
import Hero from '../components/home/Hero';
import { getHeroImageUrls } from '../services/postService';
import { getHeroPortfolioImageUrls } from '../services/portfolioService';
import { useAuth } from '../hooks/useAuth';

const SeriesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useAuth();

  const [tab, setTab] = useState('trending'); // trending | new | mine
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [heroImageUrls, setHeroImageUrls] = useState([]);

  const createParam = searchParams.get('create'); // series | episode
  const createSeriesIdParam = searchParams.get('seriesId') || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      let res;
      if (tab === 'mine') {
        res = await getUserSeries(userData?.id, 100);
      } else {
        res =
          tab === 'new'
            ? await getNewSeries({ limitCount: 24, categoryId: selectedCategory })
            : await getTrendingSeries({ limitCount: 24, categoryId: selectedCategory });
      }
      if (cancelled) return;
      let next = res.success ? (res.series || []) : [];
      if (tab === 'mine' && selectedCategory !== 'all') {
        next = next.filter((s) => (s?.categoryId || 'general') === selectedCategory);
      }
      setItems(next);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [tab, selectedCategory, userData?.id]);

  // Hero: portadas de series -> fallback portfolio/posts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const coversRes = await getSeriesCoverUrls(15);
      const covers = coversRes.success ? (coversRes.urls || []) : [];
      if (cancelled) return;
      if (covers.length >= 3) {
        setHeroImageUrls(covers);
        return;
      }
      const portfolio = await getHeroPortfolioImageUrls(15);
      if (cancelled) return;
      if (Array.isArray(portfolio) && portfolio.length >= 3) {
        setHeroImageUrls(portfolio);
        return;
      }
      const fallback = await getHeroImageUrls(15);
      if (!cancelled && Array.isArray(fallback)) setHeroImageUrls(fallback);
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleCategories = useMemo(() => CATEGORIES, []);

  const closeCreateModal = () => {
    navigate('/series', { replace: true });
  };

  const openCreateEpisodeForSeries = (seriesId) => {
    const id = (seriesId || '').trim();
    if (!id) {
      navigate('/series?create=episode');
      return;
    }
    navigate(`/series?create=episode&seriesId=${encodeURIComponent(id)}`);
  };

  return (
    <Layout>
      <div className="py-6">
        <Hero
          imageUrls={heroImageUrls}
          title="Series"
          subtitle="Crea historias por capítulos y publícalas en episodios"
          extraText="Episodios ordenados, lectura vertical y seguidores"
        />

        <div className="container-custom mt-8">
          {/* Qué es una serie */}
          <div className="mb-8 rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
            <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Qué es una serie</h2>
                <ul className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>- Una serie es una colección de episodios ordenados.</li>
                  <li>- Cada episodio es un post multi-página (lectura vertical).</li>
                  <li>- Los usuarios pueden seguir tu serie para ver nuevos episodios.</li>
                </ul>
              </div>
              <div className="flex gap-2 flex-col sm:flex-row">
                <Button variant="outline" onClick={() => navigate('/series?create=series')}>
                  Crear serie
                </Button>
                <Button variant="primary" onClick={() => navigate('/series?create=episode')}>
                  Crear episodio
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            <button
              type="button"
              onClick={() => setTab('trending')}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                tab === 'trending'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              Destacadas
            </button>
            <button
              type="button"
              onClick={() => setTab('new')}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                tab === 'new'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              Nuevas
            </button>
            {userData?.id && (
              <button
                type="button"
                onClick={() => setTab('mine')}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  tab === 'mine'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                Mis series
              </button>
            )}
          </div>

          <div className="mb-8">
            <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
              {visibleCategories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCategory(c.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all ${
                    selectedCategory === c.id
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {createParam === 'series' && (
            <CreateSeriesModal
              onClose={closeCreateModal}
              onCreated={(series) => {
                // optimista: agregar arriba
                setItems((prev) => [series, ...(prev || [])]);
                closeCreateModal();
              }}
            />
          )}

          {createParam === 'episode' && (
            <CreateEpisodeModal
              onClose={closeCreateModal}
              initialSeriesId={createSeriesIdParam}
              onCreated={(data) => {
                // llevar al detalle de la serie luego de publicar episodio
                if (data?.seriesId) {
                  navigate(`/series/${data.seriesId}`);
                } else {
                  closeCreateModal();
                }
              }}
            />
          )}

          {loading && <LoadingState message="Cargando series..." />}

          {!loading && items.length === 0 && (
            <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">No hay series todavía</h3>
              <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-xl mx-auto">
                Crea una serie para agrupar episodios, mantener el orden y permitir que la gente te siga.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/series?create=series')}>
                  Crear serie
                </Button>
                <Button variant="gradient" onClick={() => navigate('/series?create=episode')}>
                  Crear episodio
                </Button>
              </div>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 opacity-70">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-700 aspect-[3/4]" />
                ))}
              </div>
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              {tab === 'mine' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {items.map((s) => (
                    <div key={s.id} className="flex flex-col gap-2">
                      <SeriesCard series={s} />
                      <Button variant="outline" size="sm" onClick={() => openCreateEpisodeForSeries(s.id)}>
                        Nuevo episodio
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {items.map((s) => (
                    <SeriesCard key={s.id} series={s} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SeriesPage;

